/**
 * SuccessView Component - Estado de Sucesso da Tela de Resultados
 * @module features/results/components/SuccessView
 *
 * Componente que exibe o estado de sucesso quando o relatório
 * está pronto para download.
 *
 * T001-FE: Tela de Resumo com Resultados do Assessment
 *
 * Features:
 * - Mensagem de conclusão celebrativa
 * - Preview do assessment (AssessmentPreview)
 * - Botão de download em destaque
 * - Botão de novo assessment
 * - Link para agendar conversa com especialista
 * - Acessibilidade completa
 */

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ArrowRight, CheckCircle2, Download, ExternalLink, Loader2 } from 'lucide-react';
import type { SuccessViewProps } from '../types';
import { AssessmentPreview } from './AssessmentPreview';
import { DimensionScores } from './DimensionScores';
import { InsightQuote } from './InsightQuote';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Componente de sucesso para tela de resultados
 *
 * @param props - Props do componente
 * @param props.summary - Resumo do assessment
 * @param props.reportUrl - URL do relatório
 * @param props.onDownload - Handler para download
 * @param props.onNewAssessment - Handler para novo assessment
 * @param props.specialistBookingUrl - URL para agendar com especialista
 * @param props.isDownloading - Se o download está em progresso
 *
 * @example
 * ```tsx
 * <SuccessView
 *   summary={assessmentSummary}
 *   reportUrl="/api/report/123/download"
 *   onDownload={() => download()}
 *   onNewAssessment={() => navigate('/assessment')}
 *   specialistBookingUrl="https://bfranca.com/cv"
 * />
 * ```
 */
export function SuccessView({
  summary,
  reportUrl,
  onDownload,
  onNewAssessment,
  specialistBookingUrl,
  isDownloading = false,
  scores,
  insight,
  pdfReady = true,
}: SuccessViewProps) {
  const headingId = 'success-heading';

  return (
    <section
      data-testid="success-view"
      aria-labelledby={headingId}
      className={cn('flex flex-col', 'max-w-2xl mx-auto', 'p-4 md:p-8', 'gap-6')}
    >
      {/* Success Icon and Message */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <CheckCircle2
            data-testid="success-icon"
            className="h-16 w-16 text-green-500"
            aria-hidden="true"
          />
        </div>

        <div className="space-y-2">
          <h1 id={headingId} className="text-2xl md:text-3xl font-bold text-foreground">
            Review Concluído!
          </h1>
          <p className="text-muted-foreground">
            {reportUrl
              ? 'Seu diagnóstico foi gerado! Baixe o relatório completo para ver análises detalhadas e recomendações.'
              : 'Veja abaixo o resumo do seu diagnóstico.'}
          </p>
        </div>
      </div>

      {/* Assessment Preview Card */}
      <AssessmentPreview summary={summary} />

      {/* Dimension Scores */}
      {scores?.dimensions && scores.dimensions.length > 0 && (
        <DimensionScores overall={scores.overall} dimensions={scores.dimensions} />
      )}

      {/* Insight Quote */}
      {insight && <InsightQuote text={insight} />}

      {/* Action Buttons */}
      <div className="flex flex-col gap-3">
        {/* Download Button - Primary CTA */}
        <Button
          onClick={onDownload}
          size="lg"
          className="w-full h-12"
          disabled={isDownloading || !reportUrl || !pdfReady}
          aria-label={pdfReady ? 'Baixar relatório completo em formato PDF' : 'PDF sendo preparado'}
        >
          {!pdfReady ? (
            <>
              <Loader2 data-testid="pdf-preparing-spinner" className="mr-2 h-5 w-5 animate-spin" />
              Preparando PDF...
            </>
          ) : isDownloading ? (
            <>
              <Loader2 data-testid="download-spinner" className="mr-2 h-5 w-5 animate-spin" />
              Baixando...
            </>
          ) : (
            <>
              <Download data-testid="download-icon" className="mr-2 h-5 w-5" />
              Baixar Relatório Completo
            </>
          )}
        </Button>

        {/* New Assessment Button - Secondary */}
        <Button
          onClick={onNewAssessment}
          variant="outline"
          size="default"
          className="w-full"
          aria-label="Iniciar novo review"
        >
          <ArrowRight data-testid="arrow-icon" className="mr-2 h-4 w-4" />
          Iniciar Novo Review
        </Button>
      </div>

      {/* Specialist Link */}
      {specialistBookingUrl && (
        <div className="text-center pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground mb-2">Quer aprofundar seu diagnóstico?</p>
          <a
            href={specialistBookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center gap-1',
              'text-sm font-medium text-primary',
              'hover:underline focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-ring focus-visible:ring-offset-2'
            )}
          >
            Agendar Conversa com Especialista
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </div>
      )}
    </section>
  );
}

export default SuccessView;
